import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../services/auth';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const verify = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      if (!token) {
        setStatus('error');
        setError('Invalid verification link');
        return;
      }

      try {
        await auth.verifyEmail(token);
        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        setError(
          err.response?.data?.detail || 'Verification failed or link expired'
        );
      }
    };

    verify();
  }, [location.search]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
          
          {/* 🔄 LOADING */}
          {status === 'loading' && (
            <>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Verifying your email...
              </Typography>
              <Typography color="text.secondary">
                Please wait a moment
              </Typography>
            </>
          )}

          {/* ✅ SUCCESS */}
          {status === 'success' && (
            <>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ✅ Email Verified!
              </Typography>

              <Typography color="text.secondary" mb={4}>
                Your account is now active. You can log in and start using the app.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </>
          )}

          {/* ❌ ERROR */}
          {status === 'error' && (
            <>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ❌ Verification Failed
              </Typography>

              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>

              <Typography color="text.secondary" mb={4}>
                The link may be invalid or expired.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/register')}
                sx={{ mb: 2 }}
              >
                Back to Register
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </>
          )}

        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;